import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall, makeCallFile } from "utils/service";
import {
  IdentityAttributeMapping,
  IdentitySource,
  SsoConnection,
  SsoConnectionInput,
  SsoFallbackUserCandidate,
  SsoReserveInput,
} from "./SsoConfigTypes";

export const fetchAttributeDefaults = async (
  source: IdentitySource,
): Promise<Required<IdentityAttributeMapping>> => {
  try {
    const url = `${urls.IDENTITY_ATTRIBUTE_DEFAULTS}?source=${encodeURIComponent(source)}`;
    const callParams = await getCallParams("GET");
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const fetchSsoConnections = async (): Promise<SsoConnection[]> => {
  try {
    const callParams = await getCallParams("GET");
    return await makeCall(urls.SAML_CONFIG, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const reserveSsoConnector = async (
  body: SsoReserveInput,
): Promise<SsoConnection> => {
  try {
    const callParams = await getCallParams("POST", body);
    return await makeCall(urls.SAML_CONFIG, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const updateSsoConnector = async (
  registrationId: string,
  body: SsoConnectionInput,
): Promise<SsoConnection> => {
  try {
    const url = `${urls.SAML_CONFIG}/${encodeURIComponent(registrationId)}`;
    const callParams = await getCallParams("PUT", body);
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

// Reuses the same tenant-scoped user search already wired up for the Team
// "add user" picker (see screens/Settings/Team/TeamServices.ts) instead of a
// dedicated endpoint, for the SSO "password fallback user" picker.
export const searchSsoFallbackUserCandidates = async (
  search: string,
): Promise<SsoFallbackUserCandidate[]> => {
  try {
    const url = `${urls.GET_ALL_USER_WITH_SEARCH}/limit/20/page/1`;
    const callParams = await getCallParams("POST", { searchText: search });
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const deleteSsoConnector = async (registrationId: string) => {
  try {
    const url = `${urls.SAML_CONFIG}/${encodeURIComponent(registrationId)}`;
    const callParams = await getCallParams("DELETE");
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

// Activates this connector and auto-deactivates any other Active connection for
// the same account+app — the swap itself is handled transactionally server-side,
// this call is a plain fire-and-confirm with no request body.
export const activateSsoConnector = async (
  registrationId: string,
): Promise<SsoConnection> => {
  try {
    const url = `${urls.SAML_CONFIG}/${encodeURIComponent(registrationId)}/activate`;
    const callParams = await getCallParams("POST");
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

// No OIDC equivalent — there's no SP keypair/metadata to download when the
// connection is OIDC, so these stay SAML-specific rather than being renamed.
export const downloadSamlCertificate = async (
  registrationId: string,
  format: "crt" | "pem",
): Promise<Blob> => {
  try {
    const url = `${urls.SAML_CONFIG}/${encodeURIComponent(
      registrationId,
    )}/certificate?format=${format}`;
    const callParams = await getCallParams("GET");
    return await makeCallFile(url, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const downloadSamlMetadata = async (
  registrationId: string,
): Promise<Blob> => {
  try {
    const url = `${urls.SAML_CONFIG}/${encodeURIComponent(
      registrationId,
    )}/metadata`;
    const callParams = await getCallParams("GET");
    return await makeCallFile(url, callParams);
  } catch (error: any) {
    throw error;
  }
};
